/**
 * Sensor capability and the band matrices (RB §5.2.1, §5.8.1, §5.8.3).
 *
 * The rulebook keys these on the *kind* of sensor, not on the asset type — a
 * scenario may define three different radar sites that all behave as
 * `GROUND_RADAR`. Asset types carry a `sensorClass`; these tables are indexed by
 * it.
 *
 * Two different things live here and they must not be confused:
 *  - **Modifiers**, which make a roll easier or harder.
 *  - **Capability**, which decides whether the mission is legal at all. RB §5.2.1's
 *    "SDA satellites cannot perform missions against targets two or more bands
 *    away" and RB §5.8.1's "hyperspectral cannot Area Search" are legality rules,
 *    and they surface as `ORDER_INVALIDATED` with a reason rather than as an
 *    unwinnable roll (§7.3).
 */
/** RB §5.2.1 — ground SDA sensors, by target band. */
export const GROUND_SDA_BAND_MODIFIERS = Object.freeze({
    GROUND_RADAR: Object.freeze({ LEO: 4, MEO: 0, GEO: -4, HEO: -2 }),
    GROUND_OPTICAL: Object.freeze({ LEO: -2, MEO: 0, GEO: 2, HEO: 0 }),
});
/**
 * RB §5.2.1 — an SDA satellite in its own band, or one band away. Two or more
 * bands away is not a modifier; it is illegal.
 */
export const SDA_SAT_SAME_BAND_MODIFIER = 4;
export const SDA_SAT_ONE_BAND_AWAY_MODIFIER = Object.freeze({
    LEO: 0,
    MEO: 0,
    GEO: 0,
    HEO: -2,
});
/**
 * Band adjacency for the SDA satellite rule. The rulebook's bands are an ordered
 * ladder LEO–MEO–GEO with HEO alongside; HEO is treated as one band from GEO,
 * which is the only reading under which the §5.2.1 HEO column is reachable at all.
 */
const BAND_LADDER = ['LEO', 'MEO', 'GEO'];
export function bandSeparation(a, b) {
    if (a === b)
        return 0;
    if (a === 'HEO' || b === 'HEO') {
        const other = a === 'HEO' ? b : a;
        return other === 'GEO' ? 1 : 2;
    }
    return Math.abs(BAND_LADDER.indexOf(a) - BAND_LADDER.indexOf(b));
}
/** RB §5.8.1 — which ISR missions each imaging sensor class can perform at all. */
export const ISR_SENSOR_CAPABILITY = Object.freeze({
    NONE: [],
    GROUND_RADAR: [],
    GROUND_OPTICAL: [],
    SDA_SAT: [],
    PASSIVE_RF: [],
    SIGINT_SAT: [],
    EO_IMAGING: [
        'AREA_SEARCH',
        'POINT_COLLECTION',
        'DWELL',
        'TARGET_DEVELOPMENT',
        'BATTLE_DAMAGE_ASSESSMENT',
        'PATTERN_OF_LIFE',
    ],
    RADAR_IMAGING: [
        'AREA_SEARCH',
        'POINT_COLLECTION',
        'DWELL',
        'TARGET_DEVELOPMENT',
        'BATTLE_DAMAGE_ASSESSMENT',
        'PATTERN_OF_LIFE',
    ],
    HYPERSPECTRAL: ['POINT_COLLECTION', 'TARGET_DEVELOPMENT', 'BATTLE_DAMAGE_ASSESSMENT'],
    GMTI: ['AREA_SEARCH', 'POINT_COLLECTION', 'DWELL', 'PATTERN_OF_LIFE'],
});
/** RB §5.8.1 — the bracketed per-sensor bonuses on specific ISR missions. */
export const ISR_SENSOR_MISSION_MODIFIERS = Object.freeze({
    NONE: Object.freeze({}),
    GROUND_RADAR: Object.freeze({}),
    GROUND_OPTICAL: Object.freeze({}),
    SDA_SAT: Object.freeze({}),
    PASSIVE_RF: Object.freeze({}),
    SIGINT_SAT: Object.freeze({}),
    EO_IMAGING: Object.freeze({ TARGET_DEVELOPMENT: 2 }),
    RADAR_IMAGING: Object.freeze({ TARGET_DEVELOPMENT: -2 }),
    HYPERSPECTRAL: Object.freeze({ TARGET_DEVELOPMENT: 2 }),
    GMTI: Object.freeze({ DWELL: 4, PATTERN_OF_LIFE: 2 }),
});
/** RB §5.8.1 constraints, as flags the sensing phase reads. */
export const SENSOR_WEATHER_AFFECTED = Object.freeze({
    NONE: false,
    GROUND_RADAR: false,
    GROUND_OPTICAL: true,
    SDA_SAT: false,
    EO_IMAGING: true,
    RADAR_IMAGING: false, // unaffected by weather or darkness
    HYPERSPECTRAL: true,
    GMTI: false,
    PASSIVE_RF: false,
    SIGINT_SAT: false,
});
/** GMTI detects only targets that moved this turn or last (RB §5.8.1, §5.8.3). */
export const SENSOR_REQUIRES_MOVEMENT = Object.freeze({
    NONE: false,
    GROUND_RADAR: false,
    GROUND_OPTICAL: false,
    SDA_SAT: false,
    EO_IMAGING: false,
    RADAR_IMAGING: false,
    HYPERSPECTRAL: false,
    GMTI: true,
    PASSIVE_RF: false,
    SIGINT_SAT: false,
});
/** Hyperspectral ignores concealment and discriminates decoys (RB §5.8.1). */
export const SENSOR_IGNORES_DECOYS = Object.freeze({
    NONE: false,
    GROUND_RADAR: false,
    GROUND_OPTICAL: false,
    SDA_SAT: false,
    EO_IMAGING: false,
    RADAR_IMAGING: false,
    HYPERSPECTRAL: true,
    GMTI: false,
    PASSIVE_RF: false,
    SIGINT_SAT: false,
});
/** RB §5.8.3 — the ISR modifier table. */
export const ISR_MODIFIERS = Object.freeze({
    TARGET_STATIC: 4,
    GMTI_TARGET_MOVED: 4,
    /** RB §8.3 — restrictive weather over the target region, optical collectors only. */
    WEATHER_RESTRICTIVE: -4,
});
//# sourceMappingURL=sensors.js.map